import { useEffect, useRef, useState } from "react";
import { toast } from "react-toastify";
import { ITypedBaseResponse } from "@nkr/models/base-response";
import { IStrongBoxOpoListing, IStrongBoxOpoFilter, IStrongBoxOpoOrderContract } from "@nkr/models/strong-box";
import { AuthActionCreators } from "@nkr/store/authorization";
import { useAppHookContext } from "@nkr/utils/rest-api";

interface IRetVal {
    list: IStrongBoxOpoListing[],
    loading: boolean,
    error: string,
    refreshData: () => void,
    placeOrder: (order: IStrongBoxOpoOrderContract) => void
}

const useStrongBoxOpos = (filter?: IStrongBoxOpoFilter): IRetVal => {
    const { dispatch } = useAppHookContext();
    const [loading, setLoading] = useState<boolean>(false);
    const [error, setError] = useState<string>('');
    const [list, setList] = useState<IStrongBoxOpoListing[]>([]);

    const listRef = useRef<IStrongBoxOpoListing[]>(null);

    const fetchData = async () => {
        setLoading(true);

        let config = {
            url: `${__API__}/strongbox/opo/search`,
            data: filter
        };

        try {
            const response: ITypedBaseResponse<IStrongBoxOpoListing[]> = await dispatch(AuthActionCreators.apiPost(config));

            if (response && response.data.results && response.data.success) {
                setList(response.data.results);

                listRef.current = response.data.results;
            } else {
                setError('Unable to load data');
                setList([]);

                listRef.current = [];
            }
        } catch (err) {
            setList([]);
            setError(err);

            listRef.current = [];
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        setError('');

        if (filter) fetchData();
    }, [filter]);

    const placeOrder = (order: IStrongBoxOpoOrderContract) => {
        const postData = async () => {
            setLoading(true);

            let config = {
                url: `${__API__}/strongbox/opo/order`,
                data: order
            };

            try {
                const response: ITypedBaseResponse<IStrongBoxOpoListing> = await dispatch(AuthActionCreators.apiPost(config));

                if (response && response.data.results && response.data.success) {
                    let updatedList = listRef.current.map(x => {
                        if (x.opoId == response.data.results.opoId) {
                            return response.data.results;
                        }

                        return x;
                    });

                    setList(updatedList);
                } else {
                    toast.error('Unable to place order');
                }
            } catch (err) {
                toast.error(err?.response?.data ? err.response.data : 'Unable to place order');
            } finally {
                setLoading(false);
            }
        }

        postData();
    };

    const refreshData = () => {
        if (filter) fetchData();
    }

    return { loading, error, list, refreshData, placeOrder };
}

export default useStrongBoxOpos;
